import { TransactionTypeField } from './TransactionTypeField';
import { TransactionAmountField } from './TransactionAmountField';
import { TransactionDescriptionField } from './TransactionDescriptionField';
import { TransactionCategoryFieldEnhanced } from '../enhanced/TransactionCategoryFieldEnhanced';
import { TransactionDateField } from './TransactionDateField';
import { TransactionPaymentMethodField } from './TransactionPaymentMethodField';
import { TransactionCardFieldEnhanced } from '../enhanced/TransactionCardFieldEnhanced';
import { TransactionAccountField } from './TransactionAccountField';
import { TransactionInstallmentToggle } from './TransactionInstallmentToggle';
import { TransactionInstallmentField } from './TransactionInstallmentField';
import { TransactionStatusField } from './TransactionStatusField';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface TransactionFormFieldsNewProps {
  formData: any;
  amountString: string;
  errors: Record<string, string>;
  isLoading: boolean;
  availableCategories: any[];
  cards: any[];
  onInputChange: (field: string, value: any) => void;
  onAmountChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  isEditing: boolean;
}

export const TransactionFormFieldsNew = ({
  formData,
  amountString,
  errors,
  isLoading,
  availableCategories,
  cards,
  onInputChange,
  onAmountChange,
  onTypeChange,
  isEditing
}: TransactionFormFieldsNewProps) => {
  return (
    <div className="space-y-6">
      {/* Transaction Type */}
      <TransactionTypeField
        value={formData.type}
        onChange={onTypeChange}
        error={errors.type}
        disabled={isLoading}
      />

      {/* Amount */}
      <TransactionAmountField
        value={amountString}
        onChange={onAmountChange}
        error={errors.amount}
        disabled={isLoading}
      />

      {/* Description */}
      <TransactionDescriptionField
        value={formData.description}
        onChange={(value) => onInputChange('description', value)}
        error={errors.description}
        disabled={isLoading}
      />

      {/* Category */}
      <TransactionCategoryFieldEnhanced
        value={formData.categoryId}
        onChange={(value) => onInputChange('categoryId', value)}
        error={errors.categoryId}
        disabled={isLoading}
        categories={availableCategories}
      />

      {/* Date */}
      <TransactionDateField
        value={formData.date}
        onChange={(value) => onInputChange('date', value)}
        error={errors.date}
        disabled={isLoading}
      />

      {/* Payment Method */}
      <TransactionPaymentMethodField
        value={formData.paymentMethod}
        onChange={(value) => onInputChange('paymentMethod', value)}
        error={errors.paymentMethod}
        disabled={isLoading}
      />

      {/* Card Field (only for card payments AND expense transactions) */}
      {formData.paymentMethod === 'cartao' && formData.type === 'despesa' && (
        <TransactionCardFieldEnhanced
          value={formData.cardId}
          onChange={(value) => onInputChange('cardId', value)}
          error={errors.cardId}
          disabled={isLoading}
          cards={cards}
        />
      )}

      {/* Account Field (for non-card payments OR income transactions with card) */}
      {(formData.paymentMethod !== 'cartao' || formData.type === 'receita') && (
        <TransactionAccountField
          value={formData.accountId}
          onChange={(value) => onInputChange('accountId', value)}
          error={errors.accountId}
          disabled={isLoading}
          paymentMethod={formData.paymentMethod}
          transactionType={formData.type}
        />
      )}

      {/* Status Field */}
      <TransactionStatusField
        status={formData.status || 'paid'}
        onStatusChange={(status) => onInputChange('status', status)}
        disabled={isLoading}
        paymentMethod={formData.paymentMethod}
        transactionType={formData.type}
        transactionDate={formData.date}
      />

      {/* Installment Toggle */}
      <TransactionInstallmentToggle
        value={formData.isInstallment}
        onChange={(value) => onInputChange('isInstallment', value)}
        disabled={isLoading}
        paymentMethod={formData.paymentMethod}
      />

      {/* Installment Configuration */}
      {formData.isInstallment && (
        <>
          <TransactionInstallmentField
            value={formData.installments}
            onChange={(value) => onInputChange('installments', value)}
            error={errors.installments}
            disabled={isLoading}
            amount={parseFloat(amountString) || 0}
          />

          <div className="space-y-2">
            <Label htmlFor="installmentDescription">Descrição das Parcelas (Opcional)</Label>
            <Input
              id="installmentDescription"
              value={formData.installmentDescription || ''}
              onChange={(e) => onInputChange('installmentDescription', e.target.value)}
              placeholder="Ex: Compra no cartão"
              disabled={isLoading}
              className="rounded-xl"
            />
          </div>
        </>
      )}
    </div>
  );
};